import { Anchor, Box, Button, Divider, Flex, PasswordInput, Stack, Text, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { doLoginLocal, sendResetPassword } from "../../userApi";
import { emailExist } from "../admin/restQueries";
import { emailShouldLoginWithOffice365, validateEmail } from "../admin/selectors";
import loginClasses from "./LoginPage.module.css";

type LoginState = "default" | "loading" | "error";

type LoginPageProps = {
  internalOnly?: boolean;
};

const LoginPage = ({ internalOnly = false }: LoginPageProps) => {
  const [forgotPassword, setForgotPassword] = useState(false);

  const [state, setState] = useState<LoginState>("default");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailNotFoundWarning, setEmailNotFound] = useState(false);

  const [emailToSendPasswordReset, setEmailToSendPasswordReset] = useState("");
  const [emailNotFoundWarningReset, setEmailNotFoundReset] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [isResetPasswordLoading, setIsResetPasswordLoading] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);

  const isEmailValid = useMemo(() => validateEmail(email), [email]);
  const isResetEmailValid = useMemo(() => validateEmail(emailToSendPasswordReset), [emailToSendPasswordReset]);
  const [debouncedEmail] = useDebouncedValue(email, 300);
  const [debouncedResetEmail] = useDebouncedValue(emailToSendPasswordReset, 300);

  const showPasswordInput =
    isEmailValid &&
    emailShouldLoginWithOffice365(email) === false &&
    emailNotFoundWarning === false &&
    forgotPassword === false;

  const checkEmailExists = async (value: string): Promise<boolean | null> => {
    try {
      const response = await emailExist(value);
      return response.data === true;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let isCancelled = false;

    const checkEmail = async () => {
      if (forgotPassword || !validateEmail(debouncedEmail) || emailShouldLoginWithOffice365(debouncedEmail)) {
        if (isCancelled === false) {
          setEmailNotFound(false);
        }
        return;
      }

      const doesEmailExist = await checkEmailExists(debouncedEmail);
      if (isCancelled === false) {
        setEmailNotFound(doesEmailExist === false);
      }
    };

    checkEmail();

    return () => {
      isCancelled = true;
    };
  }, [debouncedEmail, forgotPassword]);

  useEffect(() => {
    let isCancelled = false;

    const checkEmail = async () => {
      if (validateEmail(debouncedResetEmail) === false || emailShouldLoginWithOffice365(debouncedResetEmail)) {
        if (isCancelled === false) {
          setEmailNotFoundReset(false);
        }
        return;
      }

      const doesEmailExist = await checkEmailExists(debouncedResetEmail);
      if (isCancelled === false) {
        setEmailNotFoundReset(doesEmailExist === false);
      }
    };

    checkEmail();

    return () => {
      isCancelled = true;
    };
  }, [debouncedResetEmail]);

  const goToEnterpriseLogin = () => {
    window.location.assign("/api/login");
  };

  const submitLocalLogin = async () => {
    setState("loading");

    try {
      await doLoginLocal(email, password);
      window.location.assign("/");
    } catch {
      setState("error");
    }
  };

  const onEmailChange = (value: string) => {
    const normalizedEmail = value.trim().toLowerCase();

    if (emailShouldLoginWithOffice365(normalizedEmail)) {
      goToEnterpriseLogin();
      return;
    }

    setState("default");
    setEmail(normalizedEmail);
    setEmailNotFound(false);
  };

  const onResetEmailChange = (value: string) => {
    const normalizedEmail = value.trim().toLowerCase();

    setEmailNotFoundReset(false);
    setResetPasswordError(null);

    if (emailShouldLoginWithOffice365(normalizedEmail)) {
      goToEnterpriseLogin();
      return;
    }

    setEmailToSendPasswordReset(normalizedEmail);
  };

  const resetPassword = async () => {
    if (isResetPasswordLoading || isResetEmailValid === false || emailNotFoundWarningReset) {
      return;
    }

    setIsResetPasswordLoading(true);
    setResetPasswordError(null);

    try {
      await sendResetPassword(emailToSendPasswordReset);
      localStorage.clear();
      deleteAllCookies();
      sessionStorage.clear();
      setIsSent(true);
    } catch {
      setResetPasswordError("Kunne ikke sende e-post for tilbakestilling akkurat nå. Prøv igjen.");
    } finally {
      setIsResetPasswordLoading(false);
    }
  };

  return (
    <Flex w="100%" h="100dvh" gap={0}>
      <Box className={loginClasses.imageBox}>
        <Text className={loginClasses.logoTitle}>MCApps</Text>
        <Text className={loginClasses.logoSubHeader}>Apper for prosjektstyring og prosjektoppfølging</Text>
      </Box>

      <Stack className={loginClasses.rightPanel} h="100%" justify="space-between" align="center" p="1em" bg="white">
        <Stack className={loginClasses.rightPanelMain} px={45} w="100%" maw={450}>
          <h2
            style={{
              color: "#272E35",
              fontWeight: 400,
              fontSize: "1.6em",
              marginBottom: "14px",
            }}
          >
            Logg inn
          </h2>

          <img src="/images/login/signInImage.png" style={{ height: "250px", objectFit: "contain" }} alt="Sign in" />

          <Button mt="sm" onClick={goToEnterpriseLogin}>
            Single Sign-on
          </Button>

          {internalOnly === false && (
            <>
              <Divider label="eller" labelPosition="center" my="sm" />

              {forgotPassword === false ? (
                <>
                  <Stack mb={15}>
                    <TextInput
                      label="Ekstern bruker"
                      placeholder="Angi epost (invitasjon kreves)"
                      value={email}
                      onChange={(event) => onEmailChange(event.currentTarget.value)}
                    />

                    {showPasswordInput && (
                      <PasswordInput
                        label="Passord"
                        placeholder="Passord"
                        value={password}
                        onChange={(event) => setPassword(event.currentTarget.value)}
                        required
                      />
                    )}

                    {emailNotFoundWarning && (
                      <Text size="sm" c="red">
                        Vi fant ikke denne e-posten i systemet vårt. Skrev du det riktig?
                      </Text>
                    )}

                    {state === "error" && isEmailValid && (
                      <Text size="sm" c="red">
                        Beklager. Feil passord
                      </Text>
                    )}

                    {isEmailValid && emailNotFoundWarning === false && (
                      <>
                        <Text size="sm">
                          Glemt passordet?{" "}
                          <Button variant="subtle" size="compact-xs" p={0} onClick={() => setForgotPassword(true)}>
                            Tilbakestill passordet ditt
                          </Button>
                        </Text>

                        {password.length > 0 && (
                          <Button
                            fullWidth
                            disabled={state === "loading" || !email || !password || emailNotFoundWarning}
                            loading={state === "loading"}
                            onClick={submitLocalLogin}
                          >
                            Logg inn
                          </Button>
                        )}
                      </>
                    )}

                    <Text size="sm" mt="sm">
                      Har du problemer med å logge inn? Kontakt oss på{" "}
                      <Anchor href="mailto:mcapps@multiconsult.no">mcapps@multiconsult.no</Anchor>.
                    </Text>
                  </Stack>
                </>
              ) : (
                <Box p="md" style={{ border: "1px solid #ccc", borderRadius: "6px" }}>
                  <Text size="sm" mb="xs">
                    {isSent
                      ? "Vi har sendt deg en e-post med lenke for å tilbakestille passordet."
                      : "Skriv inn e-postadressen din nedenfor, så sender vi deg en lenke for å tilbakestille passordet ditt."}
                  </Text>

                  <TextInput
                    label="E-post"
                    placeholder="Skriv inn epost"
                    value={emailToSendPasswordReset}
                    disabled={isSent}
                    onChange={(event) => onResetEmailChange(event.currentTarget.value)}
                  />

                  <Button
                    mt="md"
                    fullWidth
                    color={isSent ? "green" : undefined}
                    leftSection={isSent ? <i className="fa fa-check-circle" aria-hidden="true" /> : undefined}
                    disabled={
                      isSent || isResetPasswordLoading || emailNotFoundWarningReset || isResetEmailValid === false
                    }
                    loading={isResetPasswordLoading}
                    onClick={resetPassword}
                  >
                    {isSent ? "E-post sendt" : "Tilbakestill passord"}
                  </Button>

                  {resetPasswordError && (
                    <Text mt="sm" size="sm" c="red">
                      {resetPasswordError}
                    </Text>
                  )}

                  {emailNotFoundWarningReset && (
                    <Text mt="sm" size="sm" c="red">
                      Vi fant ikke denne e-posten i systemet vårt. Skrev du det riktig?
                    </Text>
                  )}

                  <Button variant="subtle" size="compact-sm" mt="sm" onClick={() => setForgotPassword(false)}>
                    Tilbake til innlogging
                  </Button>
                </Box>
              )}
            </>
          )}
        </Stack>

        <img src="/images/login/logo/multiconsultDark.png" className={loginClasses.footerLogo} alt="Multiconsult" />
      </Stack>
    </Flex>
  );
};

export default LoginPage;

function deleteAllCookies() {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i += 1) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  }
}
